import React from "react"
import { isObject } from "lodash"
import dayjs from "dayjs"

// components
import GridTypeA from "components/grids/GridTypeA/GridTypeA"

// Cell Renderers
import SiteStatusCellRenderer from "./CellRenderers/SiteStatusCellRenderer"
import Comparator from "./CellRenderers/Comparator"
import AddressComparator from "./CellRenderers/AddressComparator"
import PRSValidation from "components/ag-grid-cell-renderers/LocationsPRSValidation/PRSValidation"
import ContactComparator from "./CellRenderers/ContactComparator"
import InvestigatorComparator from "./CellRenderers/InvestigatorComparator"

// utils
import { excelStyles } from "components/Grid/ExcelStyles/excelStyles"
import { getEditColDef } from "components/grids/columnTemplates"

const StudyLocationsGrid = ({
  search,
  rowData,
  study,
  onOpenModal,
  triggerExport,
  setTriggerExport,
  permissions,
  onSelectionChanged,
  userDateFormat, // TODO: pass prop or delete
}) => {
  const [gridApi, setGridApi] = React.useState(null)

  React.useEffect(() => {
    if (gridApi) {
      if (search === "" || !search) {
        gridApi.setQuickFilter("")
      } else {
        gridApi.setQuickFilter(search)
        // deselect b/c user may not be able to see rows that they may edit/delete after filtering by search terms:
        gridApi.deselectAll()
      }
    }
  }, [search])

  React.useEffect(() => {
    if (triggerExport === true) {
      onBtnExport()
      setTriggerExport(false)
    }
  }, [triggerExport])

  const getFieldValue = (field, data, loc, headerName) => {
    if (field.includes("_prs")) {
      field = field.replace("_prs", "")
    }
    if (field === "name") {
      if (data?.location_data?.[loc]?.facility?.name) {
        return data.location_data[loc].facility.name
      }
    } else if (field === "city" || field === "state" || field === "zip" || field === "country") {
      if (data?.location_data?.[loc]?.facility?.address?.[field]) {
        return data.location_data[loc].facility.address[field]
      }
    } else if (field === "contact" || field === "contact_backup") {
      if (data?.location_data?.[loc]?.[field]) {
        return formatContact(data.location_data[loc][field])
      }
    } else if (field === "investigator" || field === "sub_investigator") {
      if (data?.location_data?.[loc]?.investigator) {
        let title = "Principal Investigator"
        if (headerName.includes("Sub-Investigator")) title = "Sub-Investigator"
        let investigators = []
        if (!Array.isArray(data.location_data[loc].investigator)) investigators = [data.location_data[loc].investigator]
        else investigators = data.location_data[loc].investigator
        let persons = investigators.filter((person) => person?.role?.includes(title))
        let personsToText = persons
          .map((person) => {
            return formatInvestigator(person)
          })
          .join(", ")
        return personsToText
      }
    } else if (data?.location_data?.[loc]?.[field]) {
      return data.location_data[loc][field]
    }
    return null
  }

  const sourceGetValue = (params) => {
    let { field, headerName } = params.colDef
    let maskVal = getFieldValue(field, params.data, "mask", headerName)
    let sourceVal = getFieldValue(field, params.data, "source", headerName)
    let ctgVal = getFieldValue(field, params.data, "ctg", headerName)
    if (maskVal !== null && maskVal !== undefined) return maskVal
    if (sourceVal) return sourceVal
    if (ctgVal) return ctgVal
    return null
  }

  const getSourceCellClasses = {
    equalBackground: (params) => {
      let { field, headerName } = params.colDef
      let maskName = getFieldValue(field, params.data, "mask", headerName)
      let sourceName = getFieldValue(field, params.data, "source", headerName)
      let ctgName = getFieldValue(field, params.data, "ctg", headerName)
      if (maskName !== undefined && maskName !== null) {
        return maskName === ctgName
      }

      return sourceName && ctgName && sourceName == ctgName
      // return ((sourceName && ctgName) && sourceName == ctgName) || (((maskName !== null && maskName !== undefined) && ctgName) && ((maskName) == ctgName));
    },
    greyBackground: (params) => {
      let { field, headerName } = params.colDef
      let maskName = getFieldValue(field, params.data, "mask", headerName)
      return maskName !== null && maskName !== undefined ? true : false
    },
    greenBackground: (params) => {
      let { field, headerName } = params.colDef
      let sourceName = getFieldValue(field, params.data, "source", headerName)

      return sourceName ? true : false
    },
    redBackground: (params) => {
      let { field, headerName } = params.colDef
      let ctgName = getFieldValue(field, params.data, "ctg", headerName)
      return ctgName ? true : false
    },
  }

  const formatContact = ({
    first_name = "",
    middle_name = "",
    last_name = "",
    degrees = "",
    phone = "",
    phone_ext = "",
    email = "",
  }) => {
    let fullName = [first_name, middle_name, last_name]
      .filter((item) => item && item.length > 0)
      .join(" ")
      .trim()
    let fullNameDegree = [fullName, degrees]
      .filter((item) => item && item.length > 0)
      .join(", ")
      .trim()
    let phoneNumber = [phone, phone_ext]
      .filter((item) => item && item.toString().length > 0)
      .join(" ext: ")
      .trim()
    return [fullNameDegree, phoneNumber, email].filter((item) => item && item.length > 0).join(", ")
  }

  const formatInvestigator = ({ first_name = "", middle_name = "", last_name = "", degrees = "" }) => {
    let fullName = [first_name, middle_name, last_name]
      .filter((item) => item && item.length > 0)
      .join(" ")
      .trim()
    let fullNameDegree = [fullName, degrees]
      .filter((item) => item && item.length > 0)
      .join(", ")
      .trim()
    return fullNameDegree
  }

  const columnDefs = [
    {
      colId: "Site Status",
      headerValueGetter: ({ location }) => {
        switch (location) {
          case "columnToolPanel":
            return "Site Status"
          default: // case "header"
            return ""
        }
      },
      cellRenderer: SiteStatusCellRenderer,
      comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
        if (nodeA?.data?.only_on_ctg) return 1
        else if (nodeA?.data?.only_on_source) return -1
        return 0
      },
      valueSetter: (params) => {
        let res = []
        if (params.data) {
          if (params.data.is_new) res.push("New")
          if (params.data.only_on_source) res.push("No CTG")
          if (params.data.only_on_ctg) res.push("No source")
          if (params.data.recruitment_status_changed) res.push("Recruitment status changed")
          return res.join(", ")
        }
        return null
      },
      getValue: (params) => {
        let res = []
        if (params.data) {
          if (params.data.is_new) res.push("New")
          if (params.data.only_on_source) res.push("No CTG")
          if (params.data.only_on_ctg) res.push("No source")
          if (params.data.recruitment_status_changed) res.push("Recruitment status changed")
          return res.join(", ")
        }
        return null
      },
      headerCheckboxSelection: true,
      checkboxSelection: true,
    },
    {
      ...getEditColDef({
        onClick: (props) => {
          if (permissions?.["study.site.edit"] && !props?.data?.only_on_ctg) onOpenModal("edit-site", props)
        },
        isAllowed: (props) => permissions?.["study.site.edit"] && !props?.data?.only_on_ctg,
        tooltip: (props) => {
          if (props?.data?.only_on_ctg)
            return "This site will not be submitted since it is not present in Site Management."
          return null
        },
      }),
      suppressColumnsToolPanel: true,
      pinned: "left",
    },
    {
      headerName: "Site Activation Date",
      field: "location_data.source.site_active_date",
      cellClass: "simpleField",
      type: "date",
    },
    {
      headerName: "Recruitment Status",
      field: "status",
      cellRenderer: Comparator,
      valueGetter: (params) => sourceGetValue(params),
      cellClassRules: getSourceCellClasses,
    },
    {
      headerName: "PRS Validation",
      field: "validations",
      valueGetter: (params) => {
        if (params?.data?.validations) {
          let validations = params?.data?.validations
          if (isObject(validations) && !Array.isArray(validations) && validations !== null) {
            validations = [validations]
          }

          let errors = []
          let nonSiteErrors = []
          let warnings = []
          let notes = []

          if (validations && validations.length > 0) {
            validations.forEach((item) => {
              if (item.severity === "ERROR") {
                if (item.site_db_id) {
                  errors.push(item)
                } else {
                  nonSiteErrors.push(item)
                }
              } else if (item.severity === "WARNING") warnings.push(item)
              else notes.push(item)
            })

            let items = []
            if (errors.length > 0) items.push(errors.length + " error(s)")
            if (warnings.length > 0) items.push(warnings.length + " warning(s)")
            if (notes.length > 0) items.push(notes.length + " note(s)")
            if (nonSiteErrors.length > 0) items.push(nonSiteErrors.length + " non-site errors")

            if (items && items.length > 0) return items.join(", ")

            return null
          }
        }
        return null
      },
      cellRenderer: PRSValidation,
      cellRendererParams: {
        source: "study-details",
        onOpenModal,
        studyData: study,
      },
      filter: false,
    },
    {
      headerName: "Facility Name",
      field: "name",
      cellRenderer: AddressComparator,
      valueGetter: (params) => sourceGetValue(params),
      cellClassRules: getSourceCellClasses,
    },
    {
      headerName: "City",
      field: "city",
      cellRenderer: AddressComparator,
      valueGetter: (params) => sourceGetValue(params),
      cellClassRules: getSourceCellClasses,
    },
    {
      headerName: "State/Province",
      field: "state",
      cellRenderer: AddressComparator,
      valueGetter: (params) => sourceGetValue(params),
      cellClassRules: getSourceCellClasses,
    },
    {
      headerName: "Country",
      field: "country",
      cellRenderer: AddressComparator,
      valueGetter: (params) => sourceGetValue(params),
      cellClassRules: getSourceCellClasses,
    },
    {
      headerName: "Postal Code",
      field: "zip",
      cellRenderer: AddressComparator,
      valueGetter: (params) => sourceGetValue(params),
      cellClassRules: getSourceCellClasses,
    },
    {
      headerName: "Site Contact",
      field: "contact",
      cellRenderer: ContactComparator,
      valueGetter: (params) => sourceGetValue(params),
      cellClassRules: getSourceCellClasses,
    },
    {
      headerName: "Backup Site Contact",
      field: "contact_backup",
      cellRenderer: ContactComparator,
      valueGetter: (params) => sourceGetValue(params),
      cellClassRules: getSourceCellClasses,
    },
    {
      headerName: "Principal Investigator",
      field: "investigator",
      cellRenderer: InvestigatorComparator,
      valueGetter: (params) => sourceGetValue(params),
      cellClassRules: getSourceCellClasses,
    },
    {
      headerName: "Sub-Investigator",
      field: "sub_investigator",
      cellRenderer: InvestigatorComparator,
      valueGetter: (params) => sourceGetValue(params),
      cellClassRules: getSourceCellClasses,
    },
    {
      headerName: "Site ID",
      field: "site_rowid",
      cellClass: "simpleField",
      valueGetter: (params) => {
        if (params?.data?.location_data?.source?.site_rowid) {
          return params.data.location_data.source.site_rowid
        } else if (params?.data?.site_id) {
          return params.data.site_id
        }
        return ""
      },
    },
    {
      headerName: "Site Number",
      field: "location_data.source.site_number",
      cellClass: "simpleField",
    },
  ]

  const defaultColDef = {
    wrapText: true,
    filter: true,
    filterParams: {
      buttons: ["clear"],
    },
    enablePivot: true,
    enableCharts: false,
    enableRowGroup: false,
    cellStyle: { whiteSpace: "pre-line" },
    autoHeight: true,
    cellClassRules: {
      oddBackground: function (params) {
        return params.rowIndex % 2 == 0
      },
    },
  }

  const gridOptions = {
    floatingFilter: false,
    columnTypes: {
      date: {
        filter: "agDateColumnFilter",
        filterParams: {
          resetButton: true,
          comparator: (filterLocalDateAtMidnight, cellValue) => {
            var cellDate, dateAsString
            dateAsString = cellValue
            if (dateAsString === null) {
              return 0
            }

            if (userDateFormat?.date_format_moment) {
              cellDate = dayjs(dateAsString, userDateFormat.date_format_moment).toDate()
            } else {
              cellDate = dayjs(dateAsString, "YYYY-MM-DD").toDate()
            }
            if (cellDate < filterLocalDateAtMidnight) {
              return -1
            } else if (cellDate > filterLocalDateAtMidnight) {
              return 1
            } else {
              return 0
            }
          },
        },
        comparator: (valueA, valueB) => {
          var cellDateA, cellDateB
          if ((valueA === null && valueB === null) || (valueA === "" && valueB === "")) {
            return 0
          } else if (valueA === null || valueA === "") {
            return -1
          } else if (valueB === null || valueB === "") {
            return 1
          }

          if (userDateFormat?.date_format_moment) {
            cellDateA = dayjs(valueA, userDateFormat.date_format_moment).toDate()
            cellDateB = dayjs(valueB, userDateFormat.date_format_moment).toDate()
          } else {
            cellDateA = dayjs(valueA).utc().toDate()
            cellDateB = dayjs(valueB).utc().toDate()
          }

          return cellDateA - cellDateB
        },
      },
    },
    excelStyles,
  }

  const isRowSelectable = React.useMemo(() => {
    return ({ data }) => {
      return data ? !data.only_on_ctg : false
    }
  }, [])

  const onGridReady = (params) => {
    setGridApi(params.api)
  }

  const onBtnExport = () => {
    const params = {
      fileName: study.study_id ? study.study_id.toLowerCase() + "_sites_data" : "study_sites_data",
      columnKeys: [
        "site_rowid",
        "location_data.source.site_number",
        "location_data.source.site_active_date",
        "status",
        "status_prs",
        "validations",
        "name",
        "name_prs",
        "city",
        "city_prs",
        "state",
        "state_prs",
        "country",
        "country_prs",
        "zip",
        "zip_prs",
        "contact",
        "contact_prs",
        "contact_backup",
        "contact_backup_prs",
        "investigator",
        "investigator_prs",
        "sub_investigator",
        "sub_investigator_prs",
      ],
      headerRowHeight: 25,
      rowHeight: 20,
    }
    gridApi.exportDataAsExcel(params)
  }

  return (
    <GridTypeA
      rowData={rowData}
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      gridOptions={gridOptions}
      onGridReady={onGridReady}
      presetGridName={"study-details-grid-" + study?.study_id}
      isRowSelectable={isRowSelectable}
      rowSelection="multiple"
      onSelectionChanged={onSelectionChanged}
      showRowCount
      pagination={true}
      paginationPageSize={20}
      style={{ minHeight: 500 }}
    />
  )
}

export default React.memo(StudyLocationsGrid)
